import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft } from 'react-icons/fi';
import { IconType } from 'react-icons';
import { dashboardService } from '../api/services/dashboardService';
import { ChatMessageResponse } from '../api/types';
import { getUserFriendlyErrorMessage } from '../api/utils/errorHandler';

interface Message {
  id: string;
  sender: string;
  message: string;
  time: string;
  isUser: boolean;
}

const ChatThreadDetail: React.FC = () => {
  const { threadId } = useParams<{ threadId: string }>();
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Format date to readable time
  const formatTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  };

  // Fetch chat messages from API
  const fetchChatMessages = async () => {
    if (!threadId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await dashboardService.getChatMessages(threadId);

      // Ensure response is an array
      if (!Array.isArray(response)) {
        console.error('Expected array but got:', response);
        setError('Mesajlar beklenmeyen formatta geldi');
        return;
      }

      // Helper function to check if content is JSON
      const isJsonString = (str: string): boolean => {
        try {
          const trimmed = str.trim();
          // Check if it starts with { or [ and ends with } or ]
          if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || 
              (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
            JSON.parse(trimmed);
            return true;
          }
          return false;
        } catch {
          return false;
        }
      };

      // Filter out messages with empty content and JSON content
      const transformedMessages: Message[] = response
        .filter((msg) => {
          if (!msg.content || msg.content.trim() === '') {
            return false; // Filter out empty messages
          }
          // Filter out JSON strings
          if (isJsonString(msg.content)) {
            return false;
          }
          return true;
        })
        .map((msg, index) => ({
          id: `${threadId}-${index}`,
          sender: msg.type === 'human' ? 'Kullanıcı' : (msg.agent_name || 'Wom AI'),
          message: msg.content || '',
          time: formatTime(new Date().toISOString()), // API doesn't provide timestamp, using current time
          isUser: msg.type === 'human',
        }));

      setMessages(transformedMessages);
    } catch (err: any) {
      const errorMessage = getUserFriendlyErrorMessage(err);
      setError(errorMessage);
      console.error('Error fetching chat messages:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChatMessages();
  }, [threadId]);

  const renderIcon = (Icon: IconType, className: string) => {
    const IconComponent = Icon as React.ComponentType<{ className?: string }>;
    return <IconComponent className={className} />;
  };

  return (
    <div className="w-full max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/chat-threads')}
          className="flex items-center text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-white mb-4 transition-colors"
        >
          {renderIcon(FiArrowLeft as IconType, "mr-2")}
          <span>Sohbet Oturumlarına Dön</span>
        </button>
        <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 dark:from-slate-200 dark:to-slate-400 bg-clip-text text-transparent mb-2">
          Konuşma Detayı
        </h1>
        <p className="text-base text-slate-600 dark:text-slate-300">Thread ID: {threadId}</p>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
          <p className="text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* Messages Container */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100 dark:border-gray-700">
        <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-800">
          <h2 className="text-xl font-semibold text-slate-800 dark:text-white">Mesajlar</h2>
        </div>
        <div className="p-6 space-y-4 max-h-[calc(100vh-300px)] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center text-slate-500 dark:text-slate-400 py-12">
              Mesaj bulunamadı
            </div>
          ) : (
            messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.isUser ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[70%] rounded-2xl px-4 py-3 ${
                  message.isUser
                    ? 'bg-indigo-600 dark:bg-indigo-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-slate-900 dark:text-white'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-xs font-semibold ${
                    message.isUser
                      ? 'text-indigo-100'
                      : 'text-slate-600 dark:text-slate-300'
                  }`}>
                    {message.sender}
                  </span>
                  <span className={`text-xs ml-2 ${
                    message.isUser
                      ? 'text-indigo-100'
                      : 'text-slate-500 dark:text-slate-400'
                  }`}>
                    {message.time}
                  </span>
                </div>
                <p className={`text-sm ${
                  message.isUser
                    ? 'text-white'
                    : 'text-slate-700 dark:text-slate-200'
                }`}>
                  {message.message}
                </p>
              </div>
            </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatThreadDetail;

